import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { CompanyModel } from '../../../../../typings/api/skymap/rest/v0/.common';
import { translateCountryByAlpha2Code } from '../../../utils/text/translation';
import { emailRegex } from '../../../utils/text/validation';
import { LabelledContainer } from '../labelled-container/labelled-container';
import { Stack } from '../stack/stack';
import { TextBox } from '../text-box/text-box';
import { ApiRequestPath } from './buy-design-license-dialog';
import { TermsOfServiceCheckBox } from './terms-of-service-check-box';

export type LicenseForm = {
  invoiceEmail: string;
  invoiceReference: string;
  invoiceTag: string;
  tosChecked: boolean;
};

type Props = {
  company: CompanyModel;
  disabled: boolean;
  id: string;
  onSubmit: (data: LicenseForm) => void;
  getErrorForPath: (path: ApiRequestPath) => string | undefined;
};

const Form = (props: Props) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    control,
  } = useForm<LicenseForm>({
    defaultValues: {
      invoiceEmail: '',
      invoiceReference: '',
      invoiceTag: '',
      tosChecked: false,
    },
  });

  React.useEffect(() => {
    setError('invoiceEmail', {
      type: 'custom',
      message: props.getErrorForPath('body.invoiceEmail'),
    });
    setError('invoiceReference', {
      type: 'custom',
      message: props.getErrorForPath('body.invoiceReference'),
    });
    setError('invoiceTag', {
      type: 'custom',
      message: props.getErrorForPath('body.invoiceTag'),
    });
  }, [props, props.getErrorForPath, setError]);

  const company = props.company;

  return (
    <form id={props.id} onSubmit={(event) => void handleSubmit(props.onSubmit)(event)}>
      <Stack spacing={1}>
        <Grid>
          <LabelledContainer
            text={t('buyDesignLicenseDialog.form.companyName', { ns: 'components' })}
          >
            {(formElementId) => (
              <TextBox disabled={true} id={formElementId()} readOnly={true} value={company?.name} />
            )}
          </LabelledContainer>

          <LabelledContainer
            text={t('buyDesignLicenseDialog.form.organizationNumber', { ns: 'components' })}
          >
            {(formElementId) => (
              <TextBox
                disabled={true}
                id={formElementId()}
                readOnly={true}
                value={company?.organizationNumber ?? ''}
              />
            )}
          </LabelledContainer>

          <LabelledContainer text={t('buyDesignLicenseDialog.form.address', { ns: 'components' })}>
            {(formElementId) => (
              <TextBox
                disabled={true}
                id={formElementId()}
                readOnly={true}
                value={company?.address}
              />
            )}
          </LabelledContainer>

          <LabelledContainer text={t('buyDesignLicenseDialog.form.zipCode', { ns: 'components' })}>
            {(formElementId) => (
              <TextBox
                disabled={true}
                id={formElementId()}
                readOnly={true}
                value={company?.zipCode}
              />
            )}
          </LabelledContainer>

          <LabelledContainer text={t('buyDesignLicenseDialog.form.city', { ns: 'components' })}>
            {(formElementId) => (
              <TextBox disabled={true} id={formElementId()} readOnly={true} value={company?.city} />
            )}
          </LabelledContainer>

          <LabelledContainer text={t('buyDesignLicenseDialog.form.country', { ns: 'components' })}>
            {(formElementId) => (
              <TextBox
                disabled={true}
                id={formElementId()}
                readOnly={true}
                value={
                  company.country
                    ? translateCountryByAlpha2Code(
                        company.country.alpha2Code as NonNullable<'DE' | 'FI' | 'NO' | 'SE'>,
                      )
                    : ''
                }
              />
            )}
          </LabelledContainer>

          <LabelledContainer
            required={true}
            text={t('buyDesignLicenseDialog.form.invoiceEmail', { ns: 'components' })}
          >
            {(formElementId) => (
              <TextBox
                {...register('invoiceEmail', {
                  required: true,
                  pattern: {
                    value: emailRegex,
                    message: t('buyDesignLicenseDialog.formValidation.invoiceEmailAddress', {
                      ns: 'components',
                    }),
                  },
                })}
                disabled={props.disabled}
                errorMessage={errors?.invoiceEmail?.message}
                id={formElementId()}
              />
            )}
          </LabelledContainer>

          <LabelledContainer
            text={t('buyDesignLicenseDialog.form.invoiceReference', { ns: 'components' })}
          >
            {(formElementId) => (
              <TextBox
                {...register('invoiceReference')}
                disabled={props.disabled}
                errorMessage={errors?.invoiceReference?.message}
                id={formElementId()}
              />
            )}
          </LabelledContainer>

          <LabelledContainer
            text={t('buyDesignLicenseDialog.form.invoiceTag', { ns: 'components' })}
          >
            {(formElementId) => (
              <TextBox
                {...register('invoiceTag')}
                disabled={props.disabled}
                errorMessage={errors?.invoiceTag?.message}
                id={formElementId()}
              />
            )}
          </LabelledContainer>
        </Grid>

        <TermsOfServiceCheckBox disabled={props.disabled} formControl={control} />
      </Stack>
    </form>
  );
};

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1em;
`;

export { Form };
